import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useCreateProvider, useProviders } from '../../hooks/useProviders';
import { useTemplates } from '../../hooks/useTemplates';
import type { CreateProviderRequest } from '../../types';
import { Loader2 } from 'lucide-react';

export const ProviderForm = () => {
  const navigate = useNavigate();
  const createProvider = useCreateProvider();
  const { data: templates } = useTemplates();
  const { data: providers } = useProviders();

  const { register, handleSubmit, formState: { errors }, watch, setError, clearErrors } = useForm<CreateProviderRequest>();

  const selectedTemplateId = watch('templateId');
  const selectedTemplate = templates?.find(t => t.id === selectedTemplateId);

  const normalizeScopes = (scopes?: string | string[], fallback?: string | string[]) => {
    const value = scopes ?? fallback ?? '';
    const scopeString = Array.isArray(value) ? value.join(' ') : value;
    return scopeString
      .split(/\s+/)
      .filter(Boolean)
      .join(' ')
      .trim();
  };

  const validateScopes = (value?: string | string[]) => {
    const normalized = normalizeScopes(value, selectedTemplate?.defaultScopes);
    if (!selectedTemplateId && !normalized) {
      return 'Scopes are required for custom providers';
    }
    return true;
  };

  const onSubmit = async (data: CreateProviderRequest) => {
    try {
      const targetScopes = normalizeScopes(
        data.scopes ?? data.scopesOverride,
        selectedTemplate?.defaultScopes
      );
      const targetClientId = (data.clientId ?? '').trim();

      const duplicate = providers?.some(p =>
        (p.clientId ?? '').trim() === targetClientId &&
        normalizeScopes(p.scopes) === targetScopes
      );

      if (duplicate) {
        setError('clientId', { type: 'manual', message: 'A provider with the same client ID and scopes already exists.' });
        setError('scopes', { type: 'manual', message: 'Duplicate client ID and scopes combination.' });
        return;
      }

      clearErrors(['clientId', 'scopes']);

      const payload: CreateProviderRequest = {
        name: data.name,
        clientId: data.clientId,
        clientSecret: data.clientSecret,
        templateId: data.templateId || null,
      };

      if (!data.templateId) {
        // Custom provider - include all required fields
        payload.authorizationUrl = data.authorizationUrl;
        payload.tokenUrl = data.tokenUrl;
        payload.scopes = data.scopes;
        payload.requiresPkce = data.requiresPkce;
      } else {
        // Template-based provider - scopes as override
        if (data.scopes) {
          payload.scopesOverride = data.scopes;
        }
      }

      await createProvider.mutateAsync(payload);
      navigate('/providers');
    } catch (error) {
      console.error('Failed to create provider:', error);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Add Identity Provider</h2>
        <p className="text-gray-600 mt-1">Configure a new OAuth provider</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-6">
        {/* Template Selection (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Template <span className="text-gray-500 font-normal">(optional)</span>
          </label>
          <select
            {...register('templateId')}
            className="input w-full"
          >
            <option value="">None - Custom Provider</option>
            {templates?.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} ({template.providerType})
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Select a template to use predefined OAuth settings, or choose "None" to configure manually
          </p>
        </div>

        {/* Basic Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
          <input
            {...register('name', { required: true })}
            type="text"
            className="input w-full"
            placeholder="My Google Account"
          />
          {errors.name && <p className="text-sm text-red-600 mt-1">Name is required</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
          <input
            {...register('clientId', { required: true })}
            type="text"
            className="input w-full"
          />
          {errors.clientId && (
            <p className="text-sm text-red-600 mt-1">
              {errors.clientId.message || 'Client ID is required'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Client Secret</label>
          <input
            {...register('clientSecret', { required: true })}
            type="password"
            className="input w-full"
          />
          {errors.clientSecret && (
            <p className="text-sm text-red-600 mt-1">Client Secret is required</p>
          )}
        </div>

        {/* Scopes (always visible, pre-filled from template) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Scopes</label>
          <input
            {...register('scopes', { validate: validateScopes })}
            type="text"
            className="input w-full"
            placeholder={
              selectedTemplate
                ? `Template default: ${
                    Array.isArray(selectedTemplate.defaultScopes)
                      ? selectedTemplate.defaultScopes.join(' ')
                      : selectedTemplate.defaultScopes
                  }`
                : 'openid profile email'
            }
          />
          <p className="text-sm text-gray-500 mt-1">
            Space-separated list of OAuth scopes
            {selectedTemplate && ' (leave empty to use template default)'}
            </p>
          {errors.scopes && <p className="text-sm text-red-600 mt-1">{errors.scopes.message || 'Scopes are required for custom providers'}</p>}
        </div>

        {/* Custom Provider Fields (only when no template selected) */}
        {!selectedTemplateId && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Authorization URL
              </label>
              <input
                {...register('authorizationUrl', { required: !selectedTemplateId })}
                type="url"
                className="input w-full"
                placeholder="https://oauth.example.com/authorize"
              />
              {errors.authorizationUrl && (
                <p className="text-sm text-red-600 mt-1">Authorization URL is required for custom providers</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Token URL</label>
              <input
                {...register('tokenUrl', { required: !selectedTemplateId })}
                type="url"
                className="input w-full"
                placeholder="https://oauth.example.com/token"
              />
              {errors.tokenUrl && <p className="text-sm text-red-600 mt-1">Token URL is required for custom providers</p>}
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('requiresPkce')}
                  className="rounded"
                />
                <span className="text-sm font-medium">Require PKCE (Proof Key for Code Exchange)</span>
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Enable PKCE for enhanced security. Most modern OAuth providers support this.
              </p>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex space-x-4 pt-4">
          <button
            type="submit"
            disabled={createProvider.isPending}
            className="btn btn-primary px-6 py-2 flex items-center space-x-2"
          >
            {createProvider.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{createProvider.isPending ? 'Creating...' : 'Create Provider'}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/providers')}
            className="btn btn-outline px-6 py-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
